import React, { useRef, FC, KeyboardEventHandler, ComponentProps } from 'react'

import { Backdrop } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import { styled as MuiStyled } from '@mui/material/styles'
import clsx from 'clsx'
import styled, { css } from 'styled-components'

import { Icon } from 'src/components/atoms'

import { useDevice } from 'src/hooks'

const CloseButtonBase = css`
  border-radius: 50%;
  // TODO [2023-09-14]: styled-componentsのthemeが使えなくなっため
  background-color: #fff;
  border: 1px solid #e2e5ea;
  filter: drop-shadow(0 5px 20px rgba(122, 137, 160, 0.1));
  position: fixed;
  z-index: 11;
  top: var(--s0);
  right: var(--s0);
  cursor: pointer;
  transition: all 0.1s ease-in-out;

  &:hover {
    filter: drop-shadow(0 2px 10px rgba(122, 137, 160, 0.2));
    transition-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: scale(0.98);
    transform-origin: 50% 100%;
  }
`

const CloseButtonContainer = styled.div`
  width: var(--s5);
  height: var(--s5);
  ${CloseButtonBase};
`
const MobileCloseButtonContainer = styled.div`
  width: var(--s4);
  height: var(--s4);
  ${CloseButtonBase};
`

const ContentContainer = styled.div`
  max-height: calc(100vh - 5.8rem);
  display: flex;
  flex-direction: column;
`
export const ModalHeaderContainer = styled.div`
  height: var(--s5);
  border-radius: 1.14rem 1.14rem 0 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`
const MainContentContainer = styled.div<{ enabledMainContentOverflow: boolean }>`
  max-height: calc(100vh - 5.8rem);
  overflow: ${({ enabledMainContentOverflow }) => (enabledMainContentOverflow ? 'auto' : 'unset')};
`

const StyledBackdrop = MuiStyled(Backdrop)`
  background-color: rgb(18 19 47 / 30%);
`

type Props = {
  isOpen: boolean
  onClose: () => void
  maxWidth?: ComponentProps<typeof Dialog>['maxWidth']
  canCloseClickOuter?: boolean
  enabledMainContentOverflow?: boolean
  noPadding?: boolean
  fullScreen?: boolean
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>
  headerInner?: React.ReactNode
  children: React.ReactNode
}

export const Modal: FC<Props> = ({
  isOpen,
  onClose,
  maxWidth = 'sm',
  canCloseClickOuter = true,
  enabledMainContentOverflow = true,
  noPadding = false,
  fullScreen = false,
  onKeyDown,
  headerInner,
  children,
}) => {
  const completeButtonReference = useRef(null)
  const { isPC } = useDevice()

  return (
    <Dialog
      open={isOpen}
      onKeyDown={onKeyDown}
      onClose={(event, reason) => {
        if (!canCloseClickOuter) {
          const mouseEvent = event as MouseEvent
          mouseEvent.stopPropagation()
          return
        }

        if (reason === 'backdropClick') {
          const mouseEvent = event as MouseEvent
          mouseEvent.stopPropagation()
        }
        onClose()
      }}
      maxWidth={maxWidth}
      fullScreen={fullScreen}
      PaperProps={{
        style: {
          borderRadius: '1.14rem',
          overflowY: 'unset',
          margin: 0,
          maxHeight: 'calc(100vh - 5.8rem)',
        },
      }}
      slots={{ backdrop: StyledBackdrop }}
      sx={{
        '.MuiDialog-scrollPaper': {
          maxHeight: '100vh',
          overflow: 'hidden',
        },
      }}
      className={`tw-fixed tw-z-50 tw-inset-0 tw-overflow-y-auto modal ${fullScreen && 'tw-m-8'}`}
    >
      <div
        className="tw-centering tw-relative"
        role="presentation"
        onClick={event => event.stopPropagation()}
      >
        {/* refを設定しないと focusable のwarningが表示される 参考: https://github.com/tailwindlabs/headlessui/issues/265 */}
        {canCloseClickOuter && isPC && (
          <CloseButtonContainer
            ref={completeButtonReference}
            className="tw-centering"
            onClick={event => {
              event.stopPropagation()
              onClose()
            }}
          >
            <Icon size="--r1" path="close/soft/line" />
          </CloseButtonContainer>
        )}

        {canCloseClickOuter && !isPC && (
          <MobileCloseButtonContainer
            ref={completeButtonReference}
            className="tw-centering"
            onClick={event => {
              event.stopPropagation()
              onClose()
            }}
          >
            <Icon size="--r0" path="close/soft/line" />
          </MobileCloseButtonContainer>
        )}

        <ContentContainer className="tw-relative">
          {headerInner && (
            <ModalHeaderContainer className="tw-bg-background-primary tw-border-b tw-border-b-border-secondary">
              <div className="tw-w-full">{headerInner}</div>
            </ModalHeaderContainer>
          )}

          <MainContentContainer
            enabledMainContentOverflow={enabledMainContentOverflow}
            className={clsx(
              !noPadding && 'tw-py-8',
              'tw-h-full',
              // childrenに高さが継承されるようflexを設定
              'tw-flex'
            )}
          >
            {children}
          </MainContentContainer>
        </ContentContainer>
      </div>
    </Dialog>
  )
}
